import { useState, useEffect } from 'react';
import {
    Button, VStack, HStack, Text, FormControl, FormLabel, FormErrorMessage,
} from '@chakra-ui/react';
import AppDrawer from '../../../components/AppDrawer';
import type { PivotDomain } from '../../../types/reporting';
import PopoverSelector from './PopoverSelector';
import type { PopoverOption } from './PopoverSelector';

const DOMAIN_OPTIONS: (PopoverOption & { icon?: string })[] = [
    { value: 'location', label: 'Location' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'vendor', label: 'Vendor' },
    { value: 'oem', label: 'OEM' },
    { value: 'estimator', label: 'Estimator' },
];

interface MetricVariantGroup {
    label: string;
    representative: string;
    variants: { value: string; label: string }[];
}

const METRIC_GROUPS: MetricVariantGroup[] = [
    {
        label: 'Order Count',
        representative: 'orderCount',
        variants: [],
    },
    {
        label: 'Orders $',
        representative: 'orderVolume',
        variants: [
            { value: 'orderVolume', label: 'Total' },
            { value: 'avgOrderSize', label: 'Avg.' },
            { value: 'medianOrderSize', label: 'Median' },
        ],
    },
    {
        label: 'Gross Margin',
        representative: 'avgGrossMarginPct',
        variants: [
            { value: 'avgGrossMarginPct', label: 'Avg.' },
            { value: 'medianGrossMarginPct', label: 'Median' },
        ],
    },
    {
        label: 'Gross Margin (w/o PM)',
        representative: 'avgGrossMarginPctWoPM',
        variants: [],
    },
    {
        label: 'Part List Price % of OE',
        representative: 'partListPricePctOfOE',
        variants: [],
    },
    {
        label: 'Missed Opportunity',
        representative: 'missedOpportunity',
        variants: [
            { value: 'missedOpportunity', label: 'Total' },
            { value: 'avgMissedOpportunity', label: 'Avg.' },
            { value: 'medianMissedOpportunity', label: 'Median' },
        ],
    },
];

const CONDENSED_METRIC_OPTIONS: PopoverOption[] = METRIC_GROUPS.map(g => ({
    value: g.representative,
    label: g.label,
}));

const METRIC_TO_GROUP = new Map<string, MetricVariantGroup>();
METRIC_GROUPS.forEach(g => {
    METRIC_TO_GROUP.set(g.representative, g);
    g.variants.forEach(v => METRIC_TO_GROUP.set(v.value, g));
});

function getDropdownValue(metric: string): string {
    return METRIC_TO_GROUP.get(metric)?.representative ?? metric;
}

function getActiveVariants(metric: string): { value: string; label: string }[] {
    const group = METRIC_TO_GROUP.get(metric);
    return group && group.variants.length >= 2 ? group.variants : [];
}

function VariantToggle({ variants, activeValue, onChange }: {
    variants: { value: string; label: string }[];
    activeValue: string;
    onChange: (value: string) => void;
}) {
    if (variants.length < 2) return null;
    return (
        <HStack spacing={0} mt={3}>
            {variants.map((v, i) => {
                const isActive = v.value === activeValue;
                const nextIsActive = i < variants.length - 1 && variants[i + 1].value === activeValue;
                return (
                    <Button
                        key={v.value}
                        size="sm"
                        h="32px"
                        flex={1}
                        borderRadius={0}
                        borderLeftRadius={i === 0 ? 'md' : 0}
                        borderRightRadius={i === variants.length - 1 ? 'md' : 0}
                        border="1px solid"
                        borderColor={isActive ? '#0071E3' : 'gray.200'}
                        borderLeftColor={i > 0 ? (isActive ? '#0071E3' : 'transparent') : undefined}
                        borderRightColor={nextIsActive ? 'transparent' : undefined}
                        ml={i > 0 ? '-1px' : undefined}
                        zIndex={isActive ? 1 : 0}
                        bg={isActive ? '#E6F1FD' : 'white'}
                        color={isActive ? '#0071E3' : 'gray.600'}
                        fontWeight={isActive ? 'semibold' : 'normal'}
                        fontSize="sm"
                        _hover={{ bg: isActive ? '#E6F1FD' : 'gray.50' }}
                        onClick={() => onChange(v.value)}
                    >
                        {v.label}
                    </Button>
                );
            })}
        </HStack>
    );
}

export interface PivotConfig {
    primaryDomain: PivotDomain;
    secondaryDomain: PivotDomain;
    metric1: string;
    metric2: string;
}

interface PivotConfigDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (config: PivotConfig) => void;
    currentConfig: PivotConfig | null;
    metricOptions: PopoverOption[];
}

interface PivotConfigPanelProps {
    currentConfig: PivotConfig | null;
    metricOptions: PopoverOption[];
    onApply: (config: PivotConfig) => void;
    onCancel?: () => void;
    showCancelButton?: boolean;
    hideButtons?: boolean;
    onConfigChange?: (config: PivotConfig, isValid: boolean) => void;
}

export const PivotConfigPanel = ({
    currentConfig,
    metricOptions,
    onApply,
    onCancel,
    showCancelButton = false,
    hideButtons = false,
    onConfigChange,
}: PivotConfigPanelProps) => {
    const [primary, setPrimary] = useState<PivotDomain>('location');
    const [secondary, setSecondary] = useState<PivotDomain>('insurance');
    const [metric1, setMetric1] = useState('orderCount');
    const [metric2, setMetric2] = useState('avgGrossMarginPct');

    useEffect(() => {
        if (currentConfig) {
            setPrimary(currentConfig.primaryDomain);
            setSecondary(currentConfig.secondaryDomain);
            setMetric1(currentConfig.metric1);
            setMetric2(currentConfig.metric2);
            return;
        }
        setPrimary('location');
        setSecondary('insurance');
        setMetric1('orderCount');
        setMetric2('avgGrossMarginPct');
    }, [currentConfig]);

    const hasDuplicateDomain = primary === secondary;

    useEffect(() => {
        onConfigChange?.({ primaryDomain: primary, secondaryDomain: secondary, metric1, metric2 }, !hasDuplicateDomain);
    }, [primary, secondary, metric1, metric2, hasDuplicateDomain, onConfigChange]);

    const secondaryOptions = DOMAIN_OPTIONS.filter(o => o.value !== primary);
    const primaryOptions = DOMAIN_OPTIONS.filter(o => o.value !== secondary);

    const handlePrimaryChange = (val: string) => {
        const v = val as PivotDomain;
        setPrimary(v);
        if (v === secondary) {
            const next = DOMAIN_OPTIONS.find(o => o.value !== v);
            if (next) setSecondary(next.value as PivotDomain);
        }
    };

    const handleSecondaryChange = (val: string) => {
        const v = val as PivotDomain;
        setSecondary(v);
        if (v === primary) {
            const next = DOMAIN_OPTIONS.find(o => o.value !== v);
            if (next) setPrimary(next.value as PivotDomain);
        }
    };

    const handleApplyClick = () => {
        if (hasDuplicateDomain) return;
        onApply({ primaryDomain: primary, secondaryDomain: secondary, metric1, metric2 });
    };

    return (
        <VStack spacing={6} align="stretch">
            <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
                Dimensions
            </Text>
            <FormControl isInvalid={hasDuplicateDomain}>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
                    Row Dimension
                </FormLabel>
                <Text fontSize="xs" color="gray.400" mb={2}>
                    Groups shown as rows in the table
                </Text>
                <PopoverSelector
                    options={primaryOptions}
                    value={primary}
                    onChange={handlePrimaryChange}
                    width="100%"
                    size="md"
                />
            </FormControl>
            <FormControl isInvalid={hasDuplicateDomain}>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
                    Column Dimension
                </FormLabel>
                <Text fontSize="xs" color="gray.400" mb={2}>
                    Groups shown as columns in the table
                </Text>
                <PopoverSelector
                    options={secondaryOptions}
                    value={secondary}
                    onChange={handleSecondaryChange}
                    width="100%"
                    size="md"
                />
                {hasDuplicateDomain && (
                    <FormErrorMessage fontSize="xs">
                        Row and column dimensions must be different.
                    </FormErrorMessage>
                )}
            </FormControl>

            <HStack pt={2}>
                <Text flex={1} fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
                    Metrics
                </Text>
            </HStack>
            <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
                    Primary Metric
                </FormLabel>
                <PopoverSelector
                    options={CONDENSED_METRIC_OPTIONS}
                    value={getDropdownValue(metric1)}
                    onChange={(val) => {
                        if (val !== getDropdownValue(metric1)) setMetric1(val);
                    }}
                    width="100%"
                    size="md"
                />
                <VariantToggle
                    variants={getActiveVariants(metric1)}
                    activeValue={metric1}
                    onChange={setMetric1}
                />
            </FormControl>
            <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
                    Secondary Metric
                </FormLabel>
                <PopoverSelector
                    options={CONDENSED_METRIC_OPTIONS}
                    value={getDropdownValue(metric2)}
                    onChange={(val) => {
                        if (val !== getDropdownValue(metric2)) setMetric2(val);
                    }}
                    width="100%"
                    size="md"
                />
                <VariantToggle
                    variants={getActiveVariants(metric2)}
                    activeValue={metric2}
                    onChange={setMetric2}
                />
            </FormControl>
            {!hideButtons && (
                <HStack justify="flex-end" pt={2}>
                    {showCancelButton && onCancel && (
                        <Button variant="outlineGray" size="sm" onClick={onCancel}>
                            Cancel
                        </Button>
                    )}
                    <Button
                        size="sm"
                        bg="#0071E3"
                        color="white"
                        _hover={{ bg: '#3545A0' }}
                        onClick={handleApplyClick}
                        isDisabled={hasDuplicateDomain}
                    >
                        Save
                    </Button>
                </HStack>
            )}
        </VStack>
    );
};

const PivotConfigDrawer = ({
    isOpen,
    onClose,
    onApply,
    currentConfig,
    metricOptions,
}: PivotConfigDrawerProps) => {
    const [draftConfig, setDraftConfig] = useState<PivotConfig | null>(null);
    const [isValid, setIsValid] = useState(true);

    const handleApply = () => {
        if (!draftConfig || !isValid) return;
        onApply(draftConfig);
        onClose();
    };

    const handleConfigChange = (config: PivotConfig, valid: boolean) => {
        setDraftConfig(config);
        setIsValid(valid);
    };

    return (
        <AppDrawer
            isOpen={isOpen}
            onClose={onClose}
            title="Configure Pivot Table"
            size="sm"
            footer={
                <>
                    <Button size="action" variant="outlineGray" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        size="action"
                        bg="#0071E3"
                        color="white"
                        _hover={{ bg: '#3544a0' }}
                        onClick={handleApply}
                        isDisabled={!isValid}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <PivotConfigPanel
                currentConfig={currentConfig}
                metricOptions={metricOptions}
                onApply={() => {}}
                hideButtons
                onConfigChange={handleConfigChange}
            />
        </AppDrawer>
    );
};

export default PivotConfigDrawer;
